import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { setApiEmpresa } from "@/lib/api";

const EmpresaContext = createContext(null);
const STORAGE_KEY = "chkr_empresa_activa";

/**
 * "Empresa activa" para el super_admin: acota TODA la interfaz a una sola empresa
 * (empleados, centros, horarios, jornadas, incidencias, reportes, usuarios, auditoría…)
 * para no revolver datos entre compañías. Sin empresa activa = vista de plataforma
 * (todas las empresas), que es el comportamiento histórico.
 *
 * Para cuentas que no son super_admin esto no aplica: siempre quedan atadas a su
 * propia empresa por el backend y el selector no se muestra.
 */
export function EmpresaProvider({ children }) {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";

  const [empresas, setEmpresas] = useState([]);
  const [empresasCargadas, setEmpresasCargadas] = useState(false);
  const [empresaActivaId, _setEmpresaActivaId] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });

  // Mantener la variable de módulo de api.js sincronizada en CADA render, antes de que
  // las páginas hijas disparen sus fetch: los efectos de los hijos corren antes que los
  // del padre, así que un useEffect aquí llegaría tarde en la primera carga. Solo el
  // super_admin propaga una empresa activa; el resto siempre va sin acotar.
  const effectiveId = isSuper ? empresaActivaId : "";
  setApiEmpresa(effectiveId || null);

  const recargarEmpresas = () =>
    api
      .get("/empresas")
      .then((r) => setEmpresas(r.data || []))
      .catch(() => setEmpresas([]))
      .finally(() => setEmpresasCargadas(true));

  useEffect(() => {
    if (!isSuper) {
      setEmpresas([]);
      return;
    }
    recargarEmpresas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuper]);

  // Si la empresa guardada ya no existe (se dio de baja, o cambió de cuenta), volver
  // a la vista de plataforma en lugar de dejar un filtro fantasma.
  useEffect(() => {
    if (!isSuper || !empresaActivaId || empresas.length === 0) return;
    if (!empresas.some((e) => e.id === empresaActivaId)) {
      _setEmpresaActivaId("");
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {}
    }
  }, [isSuper, empresaActivaId, empresas]);

  const setEmpresaActivaId = (id) => {
    const next = id || "";
    _setEmpresaActivaId(next);
    setApiEmpresa(next || null);
    try {
      if (next) localStorage.setItem(STORAGE_KEY, next);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {}
  };

  const value = useMemo(() => {
    const empresaActiva = empresas.find((e) => e.id === empresaActivaId) || null;
    // Funciones de escuela: solo si la empresa en la que se trabaja es tipo escuela.
    // super_admin -> su empresa activa (en vista de plataforma, nunca); el resto -> la
    // suya, que llega en /auth/me como empresa_tipo.
    const esEscuela = isSuper ? empresaActiva?.tipo === "escuela" : user?.empresa_tipo === "escuela";
    return {
      habilitado: isSuper,
      empresas,
      empresaActivaId: isSuper ? empresaActivaId : "",
      empresaActiva: isSuper ? empresaActiva : null,
      esVistaPlataforma: isSuper ? !empresaActivaId : false,
      esEscuela,
      // Mientras el super_admin no tiene la lista, aún no se sabe si su empresa activa es escuela.
      listo: !isSuper || !empresaActivaId || empresasCargadas,
      setEmpresaActivaId,
      recargarEmpresas,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuper, empresas, empresaActivaId, empresasCargadas, user?.empresa_tipo]);

  return <EmpresaContext.Provider value={value}>{children}</EmpresaContext.Provider>;
}

export const useEmpresa = () => useContext(EmpresaContext) || {
  habilitado: false,
  empresas: [],
  empresaActivaId: "",
  empresaActiva: null,
  esVistaPlataforma: false,
  esEscuela: false,
  listo: true,
  setEmpresaActivaId: () => {},
  recargarEmpresas: () => Promise.resolve(),
};
